import * as Yup from "yup";
import { FormikProvider, getIn, useFormik } from "formik";
import { useEffect, useState } from "react";
import { IObjectCompanies } from "../../../../types/companyaccess.type";
import { getAllCompanies } from "../../../../common/services/company.api";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import CpfDoIndicadorCheck from "./fields/CpfDoIndicadorCheck";
import { isCPF } from "../../../../common/helpers/is-cpf";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { ILead } from "../../../../types/lead.type";
import { ConsultantOption } from "@/types/employee.type";
import { getConsultantOptionsFromAPI } from "@/common/services/employee.api";

const validationSchema = Yup.object().shape({
  cpfDoIndicador: Yup.string().when("cpfDoIndicadorOptions.indicated", {
    is: true,
    then: (schema) =>
      schema
        .required("CPF do Indicador é obrigatório")
        .test("valida-cpf", "CPF inválido", (value) => isCPF(value)),
  }),
});

type FormPropsActionCommercial = {
  data: ILead;
  onSubmit: (
    values: Partial<ILead>
  ) => Promise<{ ok: boolean; data: Partial<ILead>; errors?: unknown[] }>;
  formRequestErrors?: [{ message: string; path: string[] }];
};

function FormCommecialActions(props: FormPropsActionCommercial) {
  const lead = props.data;
  const [companies, setCompanies] = useState<IObjectCompanies[]>([]);
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);
  useEffect(() => {
    async function loadCompanies() {
      const response = await getAllCompanies();
      const companies = response.data; // ICompany[] - the actual array

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mappedData = companies.map((company: any) => ({
        companyName: company.address?.city || "",
        companyId: company._id,
      })) as IObjectCompanies[];

      setCompanies(mappedData);
    }
    loadCompanies();
  }, []);

  const theInitialData = {
    cpfDoIndicadorOptions: {
      indicated: lead?.cpfDoIndicadorOptions?.indicated || false,
      notIndicated: lead?.cpfDoIndicadorOptions?.notIndicated || false,
    },
    companyId: lead.companyId || "",
    technicalConsultant: lead.technicalConsultant || "",
    cpfDoIndicador: lead.cpfDoIndicador || "",
  };
  const formik = useFormik({
    initialValues: theInitialData,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const { ok, data, errors } = await props.onSubmit(values);

        return {
          ok,
          data: data as Partial<ILead>,
          errors: errors as [{ message: string; path: string[] }],
        };
      } catch (error) {
        console.error("Erro ao salvar os dados:", error);

        return {
          ok: false,
          data: values,
          errors: [
            {
              message:
                error instanceof Error
                  ? error.message
                  : "Erro desconhecido ao processar sua solicitação",
              path: [""],
            },
          ],
        };
      }
    },
  });
  const invalidCpf = getIn(formik.errors, "cpfDoIndicador");
  useEffect(() => {
    const fetchConsultants = async () => {
      const selectedCompanyId = formik.values.companyId;

      // Se nenhuma empresa for selecionada, busca todos. Caso contrário, busca por ID.
      const options = await getConsultantOptionsFromAPI(
        selectedCompanyId ? [selectedCompanyId] : undefined,
        undefined
      );

      // Garante que o valor salvo inicialmente continue na lista
      const savedValue = props.data.technicalConsultant;
      if (savedValue && !options.some((opt) => opt.value === savedValue)) {
        options.unshift({ value: savedValue, label: savedValue });
      }

      setConsultantOptions(options);
    };

    fetchConsultants();
  }, [formik.values.companyId, props.data.technicalConsultant]);
  return (
    <form className="form-edit" onSubmit={formik.handleSubmit}>
      <section className="flex flex-col gap-4">
        <FormikProvider value={formik}>
          <section>
            <CpfDoIndicadorCheck invalidCpf={invalidCpf} />
          </section>
          <div className="flex flex-col sm:flex-row gap-3">
            <ComboBoxFormik
              required={true}
              name="companyId"
              label="Base:"
              options={companies
                .filter((company) => company.companyId && company.companyName) // Filter out invalid entries
                .map((company) => ({
                  value: company.companyId as string, // Now guaranteed to be string
                  label: company.companyName as string,
                }))}
              className="w-full"
            />
          </div>

          <ComboBoxFormik
            label="Consultor:"
            name="technicalConsultant"
            options={consultantOptions}
            required={true}
          />

          <div className="w-full flex justify-end">
            <ButtonWithLoader
              isLoading={formik.isSubmitting}
              disabled={!formik.isValid}
              onClick={formik.handleSubmit}
            >
              salvar
            </ButtonWithLoader>
          </div>
        </FormikProvider>
      </section>
    </form>
  );
}

export default FormCommecialActions;
